/**
 * forderungsmanager-master-detail — pure helper functions for the B4 drawer.
 *
 * Kept separate from the React component so every function can be unit-tested
 * in Vitest without jsdom.
 */

import type { Forderungsakte } from '@/hooks/useForderungen';

// ─── Multi-Select ─────────────────────────────────────────────────────────────

export function toggleSelection(selected: Set<string>, id: string): Set<string> {
  const next = new Set(selected);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return next;
}

export function bulkSelectAll(ids: string[]): Set<string> {
  return new Set(ids);
}

export function clearSelection(_selected: Set<string>): Set<string> {
  return new Set();
}

export function isSelected(selected: Set<string>, id: string): boolean {
  return selected.has(id);
}

export function selectionCount(selected: Set<string>): number {
  return selected.size;
}

// ─── Sticky-Footer ────────────────────────────────────────────────────────────

export function shouldShowStickyFooter(selected: Set<string>): boolean {
  return selected.size > 0;
}

// ─── Stage / Mahnstufe ────────────────────────────────────────────────────────

const MAHNSTUFE_TO_STAGE: Record<string, number> = {
  keine: 0,
  '1_mahnung': 3,
  '2_mahnung': 4,
  inkasso: 5,
  anwalt: 6,
  gerichtlich: 7,
};

const STAGE_TO_MAHNSTUFE: Record<number, Forderungsakte['mahnstufe']> = {
  0: 'keine',
  1: 'keine',
  2: 'keine',
  3: '1_mahnung',
  4: '2_mahnung',
  5: 'inkasso',
  6: 'anwalt',
  7: 'gerichtlich',
  8: 'gerichtlich',
  9: 'gerichtlich',
};

const STAGE_LABELS: Record<number, string> = {
  0: 'Forderung offen',
  1: 'Anschreiben',
  2: 'Erinnerung',
  3: '1. Mahnung',
  4: '2. Mahnung',
  5: 'Inkasso',
  6: 'Anwalt',
  7: 'Klage',
  8: 'Vollstreckung',
  9: 'Bezahlt',
};

export function mapMahnstufeToStage(mahnstufe: string): number {
  return MAHNSTUFE_TO_STAGE[mahnstufe] ?? 0;
}

export function mapStageToMahnstufe(stage: number): Forderungsakte['mahnstufe'] {
  return STAGE_TO_MAHNSTUFE[stage] ?? 'keine';
}

export function mapStageToLabel(stage: number): string {
  return STAGE_LABELS[stage] ?? STAGE_LABELS[0];
}

/** Increments stage by 1, capped at 9 (terminal). */
export function computeStageNext(current: number): number {
  if (current < 0) return 1;
  if (current >= 9) return 9;
  return current + 1;
}

// ─── Tage seit Fälligkeit ─────────────────────────────────────────────────────

export function formatTageSeitFaelligkeit(faelligAm: string | null): number {
  if (!faelligAm) return 0;
  const diff = Date.now() - new Date(faelligAm).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

// ─── Detail-Header-Felder ─────────────────────────────────────────────────────

export interface DetailHeaderFields {
  aktenzeichen: string;
  schuldner: string;
  betragOffen: number;
  stage: number;
  faelligkeit: string | null;
  verjaehrung: string | null;
}

export function buildDetailHeaderFields(
  f: Pick<
    Forderungsakte,
    | 'akten_nr'
    | 'unfallgegner_name'
    | 'gesamtbetrag'
    | 'mahnstufe'
    | 'unfall_datum'
    | 'verjährungsfrist'
  >,
): DetailHeaderFields {
  return {
    aktenzeichen: f.akten_nr ?? '—',
    schuldner: f.unfallgegner_name ?? '—',
    betragOffen: f.gesamtbetrag ?? 0,
    stage: mapMahnstufeToStage(f.mahnstufe),
    faelligkeit: f.unfall_datum,
    verjaehrung: f.verjährungsfrist,
  };
}

// ─── Filter-Logik ──────────────────────────────────────────────────────────────

export interface ForderungsFilter {
  search: string;
  status: string;
  versicherung: string;
}

export function filterForderungen<T extends Pick<Forderungsakte, 'id' | 'akten_nr' | 'unfallgegner_name' | 'status' | 'versicherung_gegner'>>(
  list: T[],
  filter: ForderungsFilter,
): T[] {
  let result = list;

  if (filter.search.trim()) {
    const q = filter.search.toLowerCase();
    result = result.filter(
      (f) =>
        f.akten_nr?.toLowerCase().includes(q) ||
        f.unfallgegner_name?.toLowerCase().includes(q),
    );
  }

  if (filter.status !== 'alle') {
    // 'bezahlt' maps to 'abgerechnet' in Forderungsakte
    const matchStatus = filter.status === 'bezahlt' ? 'abgerechnet' : filter.status;
    result = result.filter((f) => f.status === matchStatus);
  }

  if (filter.versicherung !== 'alle') {
    result = result.filter((f) => f.versicherung_gegner === filter.versicherung);
  }

  return result;
}
